import React from 'react';
import { Clock, Warning, CheckCircle, FileText } from '@phosphor-icons/react';

const timelineDotClass = (t) => {
  if (t.risk_flags?.length) return 'bg-[#FF3B30] border-[#FF3B30]';
  if (t.verified) return 'bg-[#34C759] border-[#34C759]';
  return 'bg-[#002FA7] border-[#002FA7]';
};

const severityBadgeClass = (severity) => {
  if (severity === 'critical') return 'bg-red-50 text-red-800 border-red-200';
  if (severity === 'major') return 'bg-orange-50 text-orange-800 border-orange-200';
  return 'bg-yellow-50 text-yellow-800 border-yellow-200';
};

const RISK_ROWS = [
  { k: 'retaliation', l: 'Retaliation', c: '#FF3B30' },
  { k: 'procedural', l: 'Procedural', c: '#FFCC00' },
  { k: 'evidentiary_gaps', l: 'Evidentiary Gaps', c: '#002FA7' },
];

export const SharedStatCards = ({ documents, timeline, contradictions }) => {
  const cards = [
    { label: 'Documents', value: documents.length, icon: FileText },
    { label: 'Timeline', value: timeline.length, icon: Clock },
    { label: 'Contradictions', value: contradictions.length, icon: Warning },
    { label: 'Verified', value: timeline.filter(t => t.verified).length, icon: CheckCircle },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
      {cards.map(s => (
        <div key={s.label} className="bg-white border border-[#E5E7EB] rounded-lg p-4">
          <div className="flex items-center gap-2 mb-1"><s.icon size={14} className="text-[#002FA7]" /><span className="text-xs font-bold tracking-[0.15em] uppercase text-[#6B7280] font-ibm">{s.label}</span></div>
          <p className="font-outfit text-2xl font-bold text-[#09090B]">{s.value}</p>
        </div>
      ))}
    </div>
  );
};

export const SharedOverviewTab = ({ caseData }) => {
  const risk = caseData.risk_indicators || {};
  return (
    <div className="space-y-6" data-testid="shared-overview-tab">
      {caseData.ai_summary && <div className="bg-white border border-[#E5E7EB] rounded-lg p-6">
        <h3 className="font-outfit text-lg font-semibold text-[#09090B] mb-3">Executive Summary</h3>
        <p className="text-sm text-[#09090B] font-ibm leading-relaxed whitespace-pre-wrap">{caseData.ai_summary}</p>
      </div>}
      {caseData.key_findings?.length > 0 && <div className="bg-white border border-[#E5E7EB] rounded-lg p-6">
        <h3 className="font-outfit text-lg font-semibold text-[#09090B] mb-3">Key Findings</h3>
        {caseData.key_findings.map((f, i) => <p key={`finding-${i}`} className="flex items-start gap-2 text-sm font-ibm text-[#09090B] mb-2"><CheckCircle size={16} className="text-[#34C759] mt-0.5 shrink-0" />{f}</p>)}
      </div>}
      <div className="bg-white border border-[#E5E7EB] rounded-lg p-6">
        <h3 className="font-outfit text-lg font-semibold text-[#09090B] mb-4">Risk Indicators</h3>
        <div className="grid grid-cols-3 gap-4">
          {RISK_ROWS.map(r => (
            <div key={r.k} className="text-center">
              <p className="text-xs font-bold uppercase text-[#6B7280] font-ibm mb-2">{r.l}</p>
              <div className="h-2 bg-[#F3F4F6] rounded-full overflow-hidden"><div className="h-full rounded-full" style={{ width: `${(risk[r.k] || 0) * 10}%`, backgroundColor: r.c }} /></div>
              <p className="font-outfit text-xl font-bold mt-1" style={{ color: r.c }}>{risk[r.k] || 0}/10</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export const SharedTimelineTab = ({ timeline }) => (
  <div className="relative pl-6" data-testid="shared-timeline-tab">
    <div className="absolute left-2 top-0 bottom-0 w-0.5 bg-[#E5E7EB]" />
    {timeline.map((t, i) => (
      <div key={t._id || `${t.date_iso}-${i}`} className="relative mb-5">
        <div className={`absolute -left-4 top-1 w-3 h-3 rounded-full border-2 ${timelineDotClass(t)}`} />
        <div className="bg-white border border-[#E5E7EB] rounded-lg p-4 ml-4">
          <div className="flex items-center gap-2 mb-2">
            <span className="font-ibm font-bold text-[#002FA7] text-sm">{t.date_iso}</span>
            {t.time_display && <span className="text-xs text-[#6B7280] font-ibm">{t.time_display}</span>}
            {t.verified && <span className="text-xs px-1.5 py-0.5 bg-green-50 text-green-700 rounded font-ibm">Verified</span>}
            {t.risk_flags?.map((f, j) => <span key={`rf-${f}-${j}`} className="text-xs px-1.5 py-0.5 bg-red-50 text-red-700 rounded font-ibm">{f.replace(/_/g, ' ')}</span>)}
          </div>
          {t.actors && <p className="text-xs text-[#6B7280] font-ibm mb-1"><strong>Actors:</strong> {t.actors}</p>}
          {t.ai_summary && <p className="text-sm text-[#09090B] font-ibm mb-2">{t.ai_summary}</p>}
          {t.ai_remark && <div className="p-2.5 bg-[#F9FAFB] border-l-2 border-[#002FA7] rounded-r text-xs text-[#6B7280] font-ibm italic">{t.ai_remark}</div>}
        </div>
      </div>
    ))}
  </div>
);

export const SharedContradictionsTab = ({ contradictions }) => (
  <div data-testid="shared-contradictions-tab">
    {contradictions.map((c, i) => (
      <div key={c._id || `contra-${i}`} className="bg-white border border-[#E5E7EB] rounded-lg p-5 mb-3">
        <div className="flex items-center gap-2 mb-3">
          <span className={`text-xs font-bold px-2 py-0.5 rounded border ${severityBadgeClass(c.severity)}`}>{c.severity?.toUpperCase()}</span>
          <span className="text-xs text-[#6B7280] font-ibm">{c.contradiction_type}</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
          <div className="p-3 bg-red-50/50 border border-red-100 rounded-md"><p className="text-xs font-bold text-[#6B7280] font-ibm mb-1">{c.document_a_name}</p><p className="text-sm text-[#09090B] font-ibm">{c.claim_a_text}</p></div>
          <div className="p-3 bg-red-50/50 border border-red-100 rounded-md"><p className="text-xs font-bold text-[#6B7280] font-ibm mb-1">{c.document_b_name}</p><p className="text-sm text-[#09090B] font-ibm">{c.claim_b_text}</p></div>
        </div>
        <p className="text-sm text-[#09090B] font-ibm p-3 bg-[#F9FAFB] rounded-md">{c.ai_explanation}</p>
      </div>
    ))}
  </div>
);

export const SharedDocumentsTab = ({ documents }) => (
  <div data-testid="shared-documents-tab">
    {documents.map((d, i) => (
      <div key={d._id || `doc-${i}`} className="flex items-center gap-3 bg-white border border-[#E5E7EB] rounded-lg p-4 mb-2">
        <FileText size={20} className="text-[#002FA7]" />
        <div>
          <p className="font-ibm font-semibold text-sm text-[#09090B]">{d.file_name}</p>
          <p className="text-xs text-[#6B7280] font-ibm">{d.file_type?.toUpperCase()} - {d.analysis_status}</p>
        </div>
      </div>
    ))}
  </div>
);
